import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ─── Slot group definitions ───────────────────────────────────────────────────
// Visual L→R is tile1 → tile2 → tile3 → tile4 (renamed from former 3→4→1→2).
// Migration: Flex Photo Gallery V1[i] → imagecallouts.tile{i+1}.

const tile1Def: SlotGroupDefinition = {
  label: 'Tile 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    title: { label: 'Title', type: 'text', defaultValue: 'Local Charm' },
    subtitle: { label: 'Subtitle', type: 'text', defaultValue: 'Explore The Neighborhood' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
    url: { label: 'Destination URL', type: 'url', defaultValue: '/neighborhood' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'Choose a gallery photo whose subject matches this tile\'s subtitle, "Explore The Neighborhood" — favor an exterior, streetscape, or local-area shot.',
    context: 'One of four image-callout tiles; each must use a different gallery photo. The tile\'s title and subtitle are fixed labels — use the subtitle as the reference for what the photo should depict.',
    sourceCollection: 'photo_gallery',
  },
};

const tile2Def: SlotGroupDefinition = {
  label: 'Tile 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    title: { label: 'Title', type: 'text', defaultValue: 'Unmatched Convenience' },
    subtitle: { label: 'Subtitle', type: 'text', defaultValue: 'Explore Our Amenities' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
    url: { label: 'Destination URL', type: 'url', defaultValue: '/amenities' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'Choose a gallery photo whose subject matches this tile\'s subtitle, "Explore Our Amenities" — favor a shot of a shared amenity space.',
    context: 'One of four image-callout tiles; each must use a different gallery photo. The tile\'s title and subtitle are fixed labels — use the subtitle as the reference for what the photo should depict.',
    sourceCollection: 'photo_gallery',
  },
};

const tile3Def: SlotGroupDefinition = {
  label: 'Tile 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    title: { label: 'Title', type: 'text', defaultValue: 'Your New Home Awaits' },
    subtitle: { label: 'Subtitle', type: 'text', defaultValue: 'View Floor Plans' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
    url: { label: 'Destination URL', type: 'url', defaultValue: '/floor-plans' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'Choose a gallery photo whose subject matches this tile\'s subtitle, "View Floor Plans" — favor a wide shot of the home\'s interior living spaces.',
    context: 'One of four image-callout tiles; each must use a different gallery photo. The tile\'s title and subtitle are fixed labels — use the subtitle as the reference for what the photo should depict.',
    sourceCollection: 'photo_gallery',
  },
};

const tile4Def: SlotGroupDefinition = {
  label: 'Tile 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    title: { label: 'Title', type: 'text', defaultValue: 'Luxury Design' },
    subtitle: { label: 'Subtitle', type: 'text', defaultValue: 'View The Gallery' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
    url: { label: 'Destination URL', type: 'url', defaultValue: '/gallery' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'Choose a gallery photo whose subject matches this tile\'s subtitle, "View The Gallery" — favor a photo that showcases interior design or finishes.',
    context: 'One of four image-callout tiles; each must use a different gallery photo. The tile\'s title and subtitle are fixed labels — use the subtitle as the reference for what the photo should depict.',
    sourceCollection: 'photo_gallery',
  },
};

// Layouts by breakpoint (preview: mobile / tablet 768 / desktop 1440):
//   mobile  (<640)     → 1 col, landscape 16/9
//   tablet  (640–1023) → 4-across, portrait (vertical) cards
//   desktop (≥1024)    → 4-across, landscape cards (Home desktop reference)
const TILE_SIZES = '(min-width: 640px) 25vw, 100vw';

function CalloutTileContent({
  tile,
}: {
  tile: Record<string, string | null | undefined>;
}) {
  return (
    <>
      {tile.image ? (
        <VibesitesImage
          url={tile.image}
          alt={tile.alt || ''}
          sizes={TILE_SIZES}
          className="image-callout-img absolute inset-0 z-0 w-full h-full object-cover object-center"
        />
      ) : (
        <div className="absolute inset-0 z-0 bg-[var(--vibe-color-surface)]" />
      )}

      <div className="image-callout-overlay pointer-events-none absolute inset-0 z-10 bg-black/40" />

      <div className="image-callout-copy pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center px-3 sm:px-4 lg:px-6 text-center">
        {tile.title && (
          <h3 className="image-callout-title font-heading font-bold tracking-wide text-white leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
            {tile.title}
          </h3>
        )}
        {tile.subtitle && (
          <p className="image-callout-subtitle mt-1.5 font-body font-light text-white/90 drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]">
            {tile.subtitle}
          </p>
        )}
      </div>

      <a
        href={tile.url || '#'}
        target={tile._target || '_self'}
        className="absolute inset-0 z-20"
        aria-label={tile.title || undefined}
      />
    </>
  );
}

export function ImageCallouts() {
  const tile1 = useSlotGroup('imagecallouts.tile1', tile1Def);
  const tile2 = useSlotGroup('imagecallouts.tile2', tile2Def);
  const tile3 = useSlotGroup('imagecallouts.tile3', tile3Def);
  const tile4 = useSlotGroup('imagecallouts.tile4', tile4Def);

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  return (
    <section ref={sectionRef} className="reveal-fade-up image-callouts w-full bg-white px-3 sm:px-5 md:px-6 py-3 sm:py-5 md:py-6">
      <div className="image-callouts-grid">
        <div className="image-callout group relative overflow-hidden">
          <EditableSlotGroup groupPath="imagecallouts.tile1" definition={tile1Def} className="absolute inset-0">
            <CalloutTileContent tile={tile1} />
          </EditableSlotGroup>
        </div>
        <div className="image-callout group relative overflow-hidden">
          <EditableSlotGroup groupPath="imagecallouts.tile2" definition={tile2Def} className="absolute inset-0">
            <CalloutTileContent tile={tile2} />
          </EditableSlotGroup>
        </div>
        <div className="image-callout group relative overflow-hidden">
          <EditableSlotGroup groupPath="imagecallouts.tile3" definition={tile3Def} className="absolute inset-0">
            <CalloutTileContent tile={tile3} />
          </EditableSlotGroup>
        </div>
        <div className="image-callout group relative overflow-hidden">
          <EditableSlotGroup groupPath="imagecallouts.tile4" definition={tile4Def} className="absolute inset-0">
            <CalloutTileContent tile={tile4} />
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .image-callouts-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 0.625rem;
        }
        .image-callout {
          aspect-ratio: 16 / 9;
        }
        .image-callout-title {
          font-size: 1.25rem;
        }
        .image-callout-subtitle {
          font-size: 0.75rem;
        }

        /* Tablet (preview width 768): 4 cards, portrait / vertical sizing. */
        @media (min-width: 640px) and (max-width: 1023px) {
          .image-callouts-grid {
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 0.625rem;
          }
          .image-callout {
            aspect-ratio: 3 / 4;
          }
          .image-callout-title {
            font-size: 1rem;
          }
        }

        /* Desktop (preview width 1440): 4 cards, landscape. */
        @media (min-width: 1024px) {
          .image-callouts-grid {
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 0.75rem;
          }
          .image-callout {
            aspect-ratio: 4 / 3;
          }
          .image-callout-title {
            font-size: 1.5rem;
          }
          .image-callout-subtitle {
            font-size: 0.875rem;
          }
        }

        .image-callout-img {
          transition: transform 0.6s ease;
        }
        .image-callout:hover .image-callout-img {
          transform: scale(1.06);
        }
        .image-callout-overlay {
          transition: background-color 0.3s ease;
        }
        .image-callout:hover .image-callout-overlay {
          background-color: rgba(0, 0, 0, 0.25);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ImageCallouts',
  displayName: 'Image Callouts',
  tokens: {},
  slotGroups: {
    'imagecallouts.tile1': tile1Def,
    'imagecallouts.tile2': tile2Def,
    'imagecallouts.tile3': tile3Def,
    'imagecallouts.tile4': tile4Def,
  },
};

(ImageCallouts as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ImageCallouts;
